import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2, AlertTriangle, PhoneCall, Star, Building2, Send, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toCents, formatDateTime } from "@/lib/format";
import { Amount } from "@/components/common/Amount";
import { StatusChip } from "@/components/common/StatusChip";
import { VoiceScriptPlayer } from "@/components/common/VoiceScriptPlayer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const localInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SubCard = ({ item, selected, onPick, t }) => (
  <button
    type="button"
    data-testid={`assign-sub-${item.id}`}
    onClick={() => onPick(item)}
    className={cn(
      "w-full border px-3 py-2 text-left transition-colors duration-[120ms]",
      selected ? "border-brand bg-brand-soft" : "border-line bg-paper hover:border-line-strong hover:bg-surface-sunken"
    )}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 truncate text-[13px] font-medium text-ink">
          {item.nombre}
          {item.sabe_leer === false && (
            <span className="inline-flex items-center gap-1 border border-state-warn px-1.5 text-[10.5px] font-semibold text-state-warn">
              <PhoneCall className="h-2.5 w-2.5" /> {t("subs.no_read_short")}
            </span>
          )}
        </p>
        <p className="truncate text-[11.5px] text-ink-secondary">
          {(item.especialidades_nombres || []).slice(0, 2).join(", ") || "—"}
        </p>
      </div>
      <div className="shrink-0 text-right">
        {item.tarifa_servicio_cents ? (
          <Amount cents={item.tarifa_servicio_cents} className="text-[12.5px]" />
        ) : null}
        <p className="text-[11px] text-ink-secondary">{t(`channels.${item.canal_preferido}`)}</p>
      </div>
    </div>
    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-secondary">
      <span className="inline-flex items-center gap-1">
        <Building2 className="h-3 w-3" /> {t("wo.assign.jobs_here", { n: item.trabajos_en_edificio })}
      </span>
      <span>{t("wo.assign.active_now", { n: item.ordenes_activas })}</span>
      {item.calificacion ? (
        <span className="inline-flex items-center gap-1">
          <Star className="h-3 w-3 text-state-warn" /> {item.calificacion}
        </span>
      ) : null}
    </div>
  </button>
);

export const AssignDialog = ({ open, onOpenChange, wo, onDone }) => {
  const { t } = useT();
  const [sub, setSub] = useState(null);
  const [monto, setMonto] = useState("");
  // Fix K (2026-09-28): el monto se precarga solo; si el usuario lo escribe, no se le pisa.
  const [montoTocado, setMontoTocado] = useState(false);
  const [instrucciones, setInstrucciones] = useState("");
  const [dueAt, setDueAt] = useState("");
  // Fix L (2026-09-28): la cita con el sub: día y hora («HH:MM», «manana» o «tarde»).
  const [fechaCita, setFechaCita] = useState("");
  const [horaCita, setHoraCita] = useState("");
  const [motivo, setMotivo] = useState("");
  const [motivoPrecio, setMotivoPrecio] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(null);

  const { data } = useQuery({
    queryKey: ["subs-suggest", wo?.id],
    enabled: open && !!wo?.id,
    queryFn: async () => (await api.get("/subs/suggest", { params: { work_order_id: wo.id } })).data,
  });

  useEffect(() => {
    if (!open) return;
    setSub(null);
    setSent(null);
    setMotivo("");
    setMotivoPrecio("");
    setInstrucciones(wo?.instrucciones_sub || "");
    // Fix K: si todavía no hay monto acordado, arranca en la tarifa vigente de costo del sub
    // para este edificio y servicio. Antes quedaba vacío aunque la tarifa estuviera cargada.
    const inicial = wo?.sub_cost_cents || wo?.tarifa_costo_sub_cents || 0;
    setMonto(inicial ? (inicial / 100).toFixed(2) : "");
    setMontoTocado(false);
    setDueAt(localInput(wo?.due_at));
    setFechaCita(wo?.fecha_programada || "");
    setHoraCita(wo?.hora_programada || "");
  }, [open, wo]);

  const reasignacion = !!wo?.sub_id && sub && sub.id !== wo.sub_id;
  const cents = toCents(monto);
  const excedeTarifa = !!wo?.tarifa_costo_sub_cents && cents > 0 && cents !== wo.tarifa_costo_sub_cents;
  const blocked = !sub || cents <= 0 || (reasignacion && motivo.trim().length < 5)
    || (excedeTarifa && motivoPrecio.trim().length < 5);

  const pick = (item) => {
    setSub(item);
    // Fix K (2026-09-28): orden de prioridad del monto precargado: 1) la tarifa de ESTE sub para
    // ESTE servicio, 2) la tarifa de costo vigente del edificio, 3) el promedio del sub.
    // Antes solo se cargaba si el campo estaba vacío, así que al cambiar de sub quedaba el
    // monto del anterior.
    if (montoTocado) return;
    const base = (item.tarifa_es_del_servicio && item.tarifa_servicio_cents)
      || wo?.tarifa_costo_sub_cents || item.tarifa_servicio_cents || 0;
    setMonto(base ? (base / 100).toFixed(2) : "");
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post(`/work-orders/${wo.id}/assign`, {
        sub_id: sub.id,
        sub_cost_cents: cents,
        asignar: true,
        instrucciones: instrucciones.trim() || null,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        fecha_programada: fechaCita || null,
        hora_programada: fechaCita ? (horaCita || null) : null,
        motivo: reasignacion ? motivo.trim() : null,
        motivo_precio: excedeTarifa ? motivoPrecio.trim() : null,
        notificar: true,
      });
      setSent(res.mensaje_enviado || null);
      toast.success(t("wo.assign.done"));
      onDone?.();
      if (!res.mensaje_enviado) onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wo-assign-dialog" className="max-h-[92vh] max-w-[640px] overflow-y-auto rounded-surface">
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {wo?.sub_id ? t("wo.assign.reassign") : t("wo.assign.title")}
          </DialogTitle>
          <DialogDescription className="text-[13px]">{t("wo.assign.desc")}</DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-3" data-testid="assign-sent-panel">
            <div className="flex items-center gap-2 border border-state-ok bg-state-ok-soft px-3 py-2.5">
              <CheckCircle2 className="h-4 w-4 text-state-ok" />
              <p className="text-[13px] font-medium text-state-ok">
                {t("wo.assign.sent_by", { c: t(`channels.${sent.canal}`) })}
              </p>
            </div>
            <div className="border border-line bg-surface-sunken p-3">
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <StatusChip tone={sent.canal === "voz" ? "amber" : "blue"} label={t(`channels.${sent.canal}`)} />
                <StatusChip tone="gray" testId="assign-sent-state" label={t(`messages.states.${sent.estado}`)} />
                <span className="text-[11.5px] text-ink-secondary">{sent.destinatario_contacto}</span>
              </div>
              <p data-testid="assign-sent-body" className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-ink">
                {sent.cuerpo}
              </p>
              {sent.canal === "voz" && (
                <div className="mt-2">
                  <VoiceScriptPlayer script={sent.voice_script || sent.cuerpo} label={t("messages.play_script")}
                    testId="assign-sent-play" />
                </div>
              )}
              {sent.media_url && (
                <a href={sent.media_url} target="_blank" rel="noreferrer"
                  className="mt-2 inline-block text-[12px] text-brand hover:underline">
                  {t("wo.assign.card_attached")}
                </a>
              )}
              <p className="mt-1.5 text-[11px] text-ink-tertiary">{formatDateTime(sent.created_at)}</p>
            </div>
            <DialogFooter>
              <Button data-testid="assign-close" onClick={() => onOpenChange(false)} className="h-9 bg-ink text-paper hover:opacity-90">
                {t("app.close")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div>
              <Label className="text-[12.5px]">{t("wo.assign.sub")}</Label>
              <div className="mt-1.5 max-h-[240px] space-y-1.5 overflow-y-auto pr-1" data-testid="assign-sub-list">
                {!!data?.coinciden?.length && (
                  <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
                    {t("wo.assign.matching")}
                  </p>
                )}
                {(data?.coinciden || []).map((item) => (
                  <SubCard key={item.id} item={item} t={t} selected={sub?.id === item.id} onPick={pick} />
                ))}
                {!!data?.otros?.length && (
                  <p className="pt-2 font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
                    {t("wo.assign.others")}
                  </p>
                )}
                {(data?.otros || []).map((item) => (
                  <SubCard key={item.id} item={item} t={t} selected={sub?.id === item.id} onPick={pick} />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12.5px]">{t("wo.assign.cost")} *</Label>
                <Input data-testid="wo-assign-cost" value={monto} inputMode="decimal" placeholder="0.00"
                  onChange={(e) => { setMonto(e.target.value); setMontoTocado(true); }}
                  className="mt-1 h-10 text-right font-mono text-[16px]" />
                {sub?.tarifa_servicio_cents ? (
                  <p className="mt-1 flex items-center gap-1 text-[11.5px] text-ink-secondary">
                    {t("wo.assign.suggested")}: <Amount cents={sub.tarifa_servicio_cents} className="text-[11.5px]" />
                  </p>
                ) : null}
                {wo?.tarifa_costo_sub_cents ? (
                  <p className="mt-1 text-[11.5px] text-ink-secondary" data-testid="wo-assign-rate-reference">
                    {t("wo.assign.rate_reference", { amount: `$${(wo.tarifa_costo_sub_cents / 100).toFixed(2)}` })}
                  </p>
                ) : null}
              </div>
              <div>
                <Label className="text-[12.5px]">{t("wo.due")}</Label>
                <Input data-testid="wo-assign-due" type="datetime-local" value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  className="mt-1 h-10 font-mono text-[13px]" />
              </div>
            </div>

            {/* Fix L (2026-09-28): la cita le llega al sub en el mensaje y en el link, y la torre
                avisa si no marca «Llegué» a tiempo. */}
            <div className="grid grid-cols-2 gap-3" data-testid="wo-assign-appointment">
              <div>
                <Label className="text-[12.5px]">{t("wo.assign.appointment_date")}</Label>
                <Input data-testid="wo-assign-appt-date" type="date" value={fechaCita}
                  onChange={(e) => setFechaCita(e.target.value)}
                  className="mt-1 h-10 font-mono text-[13px]" />
              </div>
              <div>
                <Label className="text-[12.5px]">{t("wo.assign.appointment_time")}</Label>
                <div className="mt-1 flex gap-1.5">
                  <Input data-testid="wo-assign-appt-time" type="time" disabled={!fechaCita}
                    value={/^\d{2}:\d{2}$/.test(horaCita) ? horaCita : ""}
                    onChange={(e) => setHoraCita(e.target.value)}
                    className="h-10 min-w-0 flex-1 font-mono text-[13px]" />
                  {["manana", "tarde"].map((f) => (
                    <button key={f} type="button" disabled={!fechaCita}
                      data-testid={`wo-assign-appt-${f}`}
                      onClick={() => setHoraCita(horaCita === f ? "" : f)}
                      className={cn("h-10 rounded-[6px] border px-2 text-[12px] disabled:opacity-50",
                        horaCita === f ? "border-brand bg-brand-soft text-brand" : "border-line text-ink-secondary")}>
                      {t(`wo.assign.slot_${f}`)}
                    </button>
                  ))}
                </div>
              </div>
              <p className="col-span-2 -mt-1 text-[11.5px] text-ink-tertiary">{t("wo.assign.appointment_hint")}</p>
            </div>

            {excedeTarifa && (
              <div>
                <Label className="text-[12.5px] text-state-critical">{t("wo.assign.price_reason")}</Label>
                <Textarea data-testid="wo-assign-price-reason" value={motivoPrecio} rows={2}
                  onChange={(e) => setMotivoPrecio(e.target.value)}
                  placeholder={t("wo.assign.price_reason_ph")}
                  className="mt-1 text-[13px]" />
              </div>
            )}

            <div>
              <Label className="text-[12.5px]">{t("wo.assign.instructions")}</Label>
              <Textarea data-testid="wo-assign-instructions" value={instrucciones} rows={2}
                onChange={(e) => setInstrucciones(e.target.value)}
                placeholder={t("wo.assign.instructions_ph")}
                className="mt-1 text-[13px]" />
            </div>

            {reasignacion && (
              <div>
                <Label className="text-[12.5px]">{t("wo.assign.reason")} *</Label>
                <Textarea data-testid="wo-assign-reason" value={motivo} rows={2}
                  onChange={(e) => setMotivo(e.target.value)} placeholder={t("wo.assign.reason_ph")}
                  className="mt-1 text-[13px]" />
                <p className="mt-1 text-[11.5px] text-state-warn">{t("wo.assign.reassign_note")}</p>
              </div>
            )}

            {sub && (
              <p className="flex items-start gap-1.5 border border-line bg-surface-sunken px-2.5 py-2 text-[11.5px] text-ink-secondary"
                data-testid="assign-channel-note">
                <Send className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                {sub.sabe_leer === false
                  ? t("wo.assign.will_call")
                  : t("wo.assign.will_send", { c: t(`channels.${sub.canal_preferido}`) })}
              </p>
            )}

            {cents <= 0 && (
              <p className="flex items-start gap-1.5 text-[11.5px] text-state-warn">
                <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                {t("wo.assign.cost_required")}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 text-[13px]">
                {t("app.cancel")}
              </Button>
              <Button data-testid="wo-assign-submit" disabled={blocked || saving} onClick={submit}
                className="h-9 text-[13px]">
                {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {t("wo.assign.submit")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
